import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

/**
 * Holds a compact description of what the learner is doing right now so the
 * floating tutor can give page-aware, level-aware help. Pages call
 * setTutorContext(...) when they mount/update and clearTutorContext() on unmount.
 *
 * Shape (all optional):
 * {
 *   page: 'conversation' | 'story' | 'journal' | 'other',
 *   page_name: string,            // for 'other'
 *   title: string,               // scenario/story title
 *   learner_role: string,
 *   ai_role: string,
 *   recent_turns: [{ speaker, text }],
 *   options: [string],           // current suggested replies
 *   corrections: [{ original, correction }],
 *   draft: string,               // journal draft
 *   proficiency_level: string    // A1..C2
 * }
 */
const TutorContext = createContext({
  tutorContext: null,
  setTutorContext: () => {},
  clearTutorContext: () => {},
  registerInputInsertHandler: () => {},
  clearInputInsertHandler: () => {},
  insertIntoInput: () => false,
});

export const TutorProvider = ({ children }) => {
  const [tutorContext, setTutorContextState] = useState(null);

  const setTutorContext = useCallback((ctx) => {
    setTutorContextState(ctx);
  }, []);

  const clearTutorContext = useCallback(() => {
    setTutorContextState(null);
  }, []);

  // A page (e.g. LearningSessionPage) registers a handler that lets the floating
  // tutor push a chosen suggested response into that page's chat input. A ref is
  // used so registering doesn't re-render the tree.
  const insertHandlerRef = useRef(null);
  const registerInputInsertHandler = useCallback((fn) => {
    insertHandlerRef.current = fn;
  }, []);
  const clearInputInsertHandler = useCallback(() => {
    insertHandlerRef.current = null;
  }, []);
  // Returns true if a page consumed the text, false if no input is available
  // (so the tutor can hide/disable the "use this response" button).
  const insertIntoInput = useCallback((text) => {
    if (typeof insertHandlerRef.current === 'function') {
      insertHandlerRef.current(text);
      return true;
    }
    return false;
  }, []);

  return (
    <TutorContext.Provider
      value={{
        tutorContext,
        setTutorContext,
        clearTutorContext,
        registerInputInsertHandler,
        clearInputInsertHandler,
        insertIntoInput,
      }}
    >
      {children}
    </TutorContext.Provider>
  );
};

export const useTutorContext = () => useContext(TutorContext);

export default TutorContext;
